import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomDatePicker from "../components/CustomDatePicker";
import BookingSuccessModal from "../components/BookingSuccessModal";
import guestHouseService from "../services/guestHouseService";
import bookingService from "../services/bookingService";
import fileService from "../services/fileService"; // Import the file service
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";

const GuestHouseDetail = () => {
  // Get ID from URL parameters
  const { id } = useParams();
  const navigate = useNavigate();
  const {  currentUser } = useAuth();
  
  console.log("Auth Error:", currentUser);
  useEffect(() => {
    // Safely get user from localStorage or context
    console.log("Auth Error2:", currentUser);
  }, [currentUser]);
  // State management
  const [guestHouse, setGuestHouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingId, setBookingId] = useState(null); // Store the booking ID after successful booking
  const [uploadedFiles, setUploadedFiles] = useState([]); // Track uploaded files
  const [fileUploading, setFileUploading] = useState(false); // Track file upload status
  
  const [formData, setFormData] = useState({
    guestName: "",
    cpfNumber: "",
    emailId: "",
    phoneNo: "",
    checkInDate: "",
    checkOutDate: "",
    travelType: "",
    roomRent: 0,
    finalRoomRent: 0,
    selectedRoomId: "",
    roomType: "",
    roomNumber: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const { protocol, hostname, port } = window.location;
  const portSuffix = port ? `:${port}` : "";
  const trailingSlash = "/";

  const domain = `${protocol}//${hostname}${portSuffix}${trailingSlash}assets`;
  
  // Fetch guest house details on component mount
  useEffect(() => {
    const fetchGuestHouseDetails = async () => {
      try {
        setLoading(true);
        const data = await guestHouseService.getGuestHouseById(id, true);
        setGuestHouse(data);

        // Set the first active room as default selection
        const activeRooms = data.rooms ? data.rooms.filter(room => room.isActive) : [];
        if (activeRooms.length > 0) {
          const defaultRoom = activeRooms[0];
          setFormData((prev) => ({
            ...prev,
            selectedRoomId: defaultRoom.roomID,
            roomRent: defaultRoom.rentPerDay,
            finalRoomRent: defaultRoom.rentPerDay,
            roomType: defaultRoom.roomType,
            roomNumber: defaultRoom.roomNumber,
          }));
        }
      } catch (err) {
        console.error("Error fetching guest house details:", err);
        setError(err.message || "Failed to load guest house details");
        toast.error("Failed to load guest house details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchGuestHouseDetails();
    }
  }, [id]);
  
  // Format disabled date ranges for the DatePicker
  const formatDisabledDateRanges = (selectedRoomId) => {
    if (!selectedRoomId || !guestHouse?.rooms) return [];
    
    const selectedRoom = guestHouse.rooms.find(room => 
      room.roomID.toString() === selectedRoomId.toString()
    );
    
    if (!selectedRoom || !selectedRoom.occupiedDates || selectedRoom.occupiedDates.length === 0) {
      return [];
    }
    
    return selectedRoom.occupiedDates.map(dateRange => ({
      start: new Date(dateRange.checkInDate),
      end: new Date(dateRange.checkOutDate)
    }));
  };
  
  // Validate date range against disabled ranges
  const validateDateRange = (checkInDate, checkOutDate, disabledRanges) => {
    if (!checkInDate || !checkOutDate) return true;
    
    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);
    
    // Check if date range overlaps with any disabled range
    return !disabledRanges.some(range => 
      start <= new Date(range.end) && new Date(range.start) <= end
    );
  };
  
  const formatAddress = (address, city, state, separator = ", ") => {
    const hasCity = city && city.trim().length > 0;
    const hasState = state && state.trim().length > 0;
    const hasAddress = address && address.trim().length > 0;
    var response = "";
    if (hasCity && hasState) {
      response = `${city.trim()}${separator}${state.trim()}`;
    } else if (hasCity) {
      response = city.trim();
    } else if (hasState) {
      response = state.trim();
    }
    response = hasAddress
      ? `${address.trim()}${separator}${response}`
      : response;
    return response;
  };
  
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    
    // Create a copy of the current form data with the new value
    const updatedFormData = { ...formData, [id]: value };
    
    // Special validation for phone numbers - only allow digits
    if (id === "phoneNo") {
      // If non-digit characters are entered, don't update the field
      if (value !== "" && !/^\d*$/.test(value)) {
        return; // Don't update if non-digit characters are entered
      }
      
      // Clear validation error if the phone number becomes valid
      if (isValidPhoneNumber(value) && validationErrors.phoneNo) {
        setValidationErrors({
          ...validationErrors,
          phoneNo: ""
        });
      } else if (!(isValidPhoneNumber(value) && validationErrors.phoneNo) && (value.length>0 && value.length < 10)) {
        setValidationErrors({
          ...validationErrors,
          phoneNo: "Please enter a valid phone number"
        });
      }
    }
    
    // Special validation for email - clear error if valid
    if (id === "emailId") {
      if ((value === "" || isValidEmail(value)) && validationErrors.emailId) {
        setValidationErrors({
          ...validationErrors,
          emailId: ""
        });
      }else if (!(isValidEmail(value) && validationErrors.emailId) && (value.length>0 && !isValidEmail(value))) {
        setValidationErrors({
          ...validationErrors,
          emailId: "Please enter a valid email address"
        });
      }
    }
    
    // Clear general validation error for this field when user starts typing
    if (validationErrors[id] && id !== "phoneNo" && id !== "emailId") {
      setValidationErrors({
        ...validationErrors,
        [id]: ""
      });
    }
    
    // Handle room selection changes
    if (id === "selectedRoomId") {
      // When room selection changes, update the room rent and room details
      const selectedRoom = guestHouse.rooms.find(room => room.roomID.toString() === value);
      if (selectedRoom) {
        const baseRent = selectedRoom.rentPerDay;
        let finalRent = baseRent;
        
        // Apply discount for official travel if that's selected
        if (formData.travelType === "official") {
          finalRent = baseRent * 0.8; // 20% discount for official travel
        }
        
        setFormData({
          ...formData,
          [id]: value,
          roomRent: baseRent,
          finalRoomRent: finalRent,
          roomType: selectedRoom.roomType,
          roomNumber: selectedRoom.roomNumber,
          // Reset dates when room changes to avoid conflicts
          checkInDate: "",
          checkOutDate: ""
        });
        return;
      }
    }
    
    // For check-in date or check-out date, validate date range immediately
    if (id === "checkInDate" || id === "checkOutDate") {
      // Check if the date range is valid
      const disabledRanges = formatDisabledDateRanges(updatedFormData.selectedRoomId);
      
      // Additional validation for check-out being after check-in
      if (id === "checkInDate" && updatedFormData.checkOutDate) {
        if (new Date(value) >= new Date(updatedFormData.checkOutDate)) {
          // Reset check-out date if check-in date is after it
          updatedFormData.checkOutDate = "";
        }
      }
      
      // Only validate if both dates are selected
      if (updatedFormData.checkInDate && updatedFormData.checkOutDate) {
        const isValidRange = validateDateRange(
          updatedFormData.checkInDate,
          updatedFormData.checkOutDate,
          disabledRanges
        );
        
        if (!isValidRange) {
          setValidationErrors({
            ...validationErrors,
            dateConflict: "Selected dates overlap with already booked dates",
            [id]: "Date conflict"
          });
        }
      }
    }
    
    setFormData(updatedFormData);
  
    // If travel type changes, recalculate final room rent
    if (id === "travelType") {
      calculateFinalRent(value);
    }
  };

  // Calculate final room rent based on travel type
  const calculateFinalRent = (travelType) => {
    const baseRent = formData.roomRent || 0;
    let finalRent = baseRent;

    // Apply discount for official travel (example logic)
    if (travelType === "official") {
      finalRent = baseRent * 0.8; // 20% discount for official travel
    }

    setFormData((prev) => ({
      ...prev,
      finalRoomRent: finalRent,
    }));
  };

  // Email validation function
  const isValidEmail = (email) => { 
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(email);
    };

  // Updated phone number validation function to require exactly 10 digits
  const isValidPhoneNumber = (phone) => {
    // Check if input contains exactly 10 digits (0-9)
    const phonePattern = /^\d{10}$/;
    return phonePattern.test(phone);
  };

  // Validate form fields
  const validateForm = () => {
    const errors = {};
    
    // Check required fields
    if (!formData.guestName.trim()) {
      errors.guestName = "Guest name is required";
    }
    
    if (!formData.phoneNo.trim()) {
      errors.phoneNo = "Phone number is required";
    } else if (!isValidPhoneNumber(formData.phoneNo)) {
      errors.phoneNo = "Please enter a valid phone number";
    }
    
    if (!formData.checkInDate) {
      errors.checkInDate = "Check-in date is required";
    }
    
    if (!formData.checkOutDate) {
      errors.checkOutDate = "Check-out date is required";
    }
    
    if (!formData.selectedRoomId) {
      errors.selectedRoomId = "Room selection is required";
    }
    
    if (!formData.travelType) {
      errors.travelType = "Travel type is required";
    }
    
    // Check if date range is valid against disabled dates
    if (formData.checkInDate && formData.checkOutDate) {
      const disabledRanges = formatDisabledDateRanges(formData.selectedRoomId);
      const isValidRange = validateDateRange(
        formData.checkInDate,
        formData.checkOutDate,
        disabledRanges
      );
      
      if (!isValidRange) {
        errors.dateConflict = "Selected dates overlap with already booked dates";
        errors.checkInDate = "Date conflict";
        errors.checkOutDate = "Date conflict";
      }
    }
    
    // Email validation - only validate if an email is provided
    if (formData.emailId && !isValidEmail(formData.emailId)) {
      errors.emailId = "Please enter a valid email address";
    }
    
    return errors;
  };

  // Close the success modal
  const handleCloseModal = () => {
    setShowSuccessModal(false);
    // Optionally navigate to bookings list or refresh the page
    // navigate("/bookings");
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const errors = validateForm();
    
    // If there are validation errors, show them and stop form submission
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Show toast for validation errors
      if (errors.dateConflict) {
        toast.error(errors.dateConflict);
      } else {
        toast.error("Please fill all required fields correctly");
      }
      return;
    }

    // When preparing data to send
    const bookingData = {
      guestName: formData.guestName,
      cpfNumber: formData.cpfNumber,
      emailId: formData.emailId || "",
      phoneNo: formData.phoneNo,
      
      // Rename these fields to match the API expectations
      checkInDateTime: formData.checkInDate,
      checkOutDateTime: formData.checkOutDate,
      travelType: formData.travelType,
      roomRent: formData.roomRent,
      finalRoomRent: formData.finalRoomRent,
      roomId: formData.selectedRoomId,
      roomType: formData.roomType,
      roomNumber: formData.roomNumber,
      guesthouseId: id || 4
    };

    try {
      // Call the booking API
      const response = await bookingService.createBooking(bookingData);

      // Set the booking ID for file uploads
      setBookingId(response.bookingId);

      // Show success message
      toast.success("Booking successful!");
      
      // Show the success modal instead of navigating to a new page
      setShowSuccessModal(true);
    } catch (err) {
      toast.error("Booking failed. Please try again.");
      console.error("Booking error:", err);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    // Clear any previous file errors
    if (validationErrors.file) {
      setValidationErrors({
        ...validationErrors,
        file: null
      });
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    const fileInput = document.getElementById('fileInput');
    const file = fileInput.files[0];
    
    // Validate file is selected
    if (!file) {
      setValidationErrors({
        ...validationErrors,
        file: "Please select a file to upload"
      });
      return;
    }
    
    // Validate file type (PDF, JPEG, JPG, PNG)
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setValidationErrors({
        ...validationErrors,
        file: "Invalid file type. Only PDF, JPEG, JPG, and PNG are allowed."
      });
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 50 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setValidationErrors({
        ...validationErrors,
        file: "File size exceeds 50MB limit."
      });
      return;
    }
    
    // Create form data for upload
    const formData = new FormData();
    formData.append('file', file);
    
    // Add booking or guesthouse ID depending on context
    if (bookingId) {
      formData.append('bookingId', bookingId);
    } else {
      formData.append('guesthouseId', id);
    }
    
    // Add description (optional)
    const fileDescription = document.getElementById('fileDescription').value;
    if (fileDescription) {
      formData.append('description', fileDescription);
    }
    
    try {
      setFileUploading(true);
      
      // Upload the file
      const response = await fileService.uploadFile(formData);
      
      // Add the new file to the list of uploaded files
      setUploadedFiles([...uploadedFiles, response]);
      
      // Clear the file input and description
      fileInput.value = '';
      document.getElementById('fileDescription').value = '';
      
      toast.success("File uploaded successfully!");
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setFileUploading(false);
    }
  };

  // Room Occupied Dates Component
  const RoomOccupiedDates = ({ selectedRoomId }) => {
    if (!selectedRoomId || !guestHouse?.rooms) return null;
    
    const selectedRoom = guestHouse.rooms.find(room => 
      room.roomID.toString() === selectedRoomId.toString()
    );
    
    if (!selectedRoom || !selectedRoom.occupiedDates || selectedRoom.occupiedDates.length === 0) {
      return null;
    }
    
    return (
      <div className="mt-4 p-3 border rounded bg-light">
        <h5 className="mb-3">Already Booked Dates for {selectedRoom.roomName}</h5>
        <ul className="list-group">
          {selectedRoom.occupiedDates.map((dateRange, index) => {
            const checkIn = new Date(dateRange.checkInDate);
            const checkOut = new Date(dateRange.checkOutDate);
            return (
              <li key={index} className="list-group-item list-group-item-danger">
                <i className="fas fa-calendar-times me-2"></i>
                {checkIn.toLocaleDateString()} to {checkOut.toLocaleDateString()}
              </li>
            );
          })}
        </ul>
        <p className="text-muted mt-2 small">These dates are not available for booking.</p>
      </div>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <>
        <Navbar />
        <main className="container mt-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading guest house details...</p>
        </main>
        <Footer />
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <Navbar />
        <main className="container mt-5 text-center">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate("/guesthouses")}
          >
            Back to Guest Houses
          </button>
        </main>
        <Footer />
      </>
    );
  }

  // If no guest house data
  if (!guestHouse) {
    return (
      <>
        <Navbar />
        <main className="container mt-5 text-center">
          <div className="alert alert-warning" role="alert">
            Guest house not found
          </div>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate("/guesthouses")}
          >
            Back to Guest Houses
          </button>
        </main>
        <Footer />
      </>
    );
  }
  
  const imageUrl = domain + "/images/1.png";
  
  // Required field indicator styles
  const requiredField = <span style={{ color: 'red' }}>*</span>;
  
  return (
    <>
      <Navbar />
      <main className="container mt-3">
        {/* Hero image and guest house details */}
        <div className="row">
          <div className="col-12">
            <img
              src={imageUrl}
              alt={guestHouse.name || "ONGC Guest House1"}
              className="hero-image img-fluid"
              style={{
                borderRadius: "5px",
                width: "100%",
                maxHeight: "400px",
                objectFit: "cover",
              }}
            />
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-md-5">
            <h2 className="guest-house-title">
              {guestHouse.guestHouseName || "ONGC Guest House1"}
            </h2>
            <div className="location-details">
              <p className="mb-1">
                {formatAddress(
                  guestHouse.address,
                  guestHouse.city,
                  guestHouse.state
                ) || ""}
              </p>
              <p>
                {guestHouse.description ||
                  guestHouse.additional_tx ||
                  "Consisting of two suites, nine rooms, Reception area, dining hall and kitchen"}
              </p>
            </div>

            <div className="feature-icons d-flex gap-4 mb-4">
              <div className="feature-icon d-flex align-items-center">
                <i className="fas fa-couch me-2"></i>
                <span>{guestHouse.totalRooms || 9} Rooms</span>
              </div>
              <div className="feature-icon d-flex align-items-center">
                <i className="fas fa-couch me-2"></i>
                <span>{guestHouse.suites || 2} Suites</span>
              </div>
              <div className="feature-icon d-flex align-items-center">
                <i className="fas fa-location-arrow me-2 text-primary"></i>
                <a href={guestHouse.directionUrl} target="_blank" rel="noopener noreferrer" className="text-primary">
                  Location
                </a>
              </div>
            </div>

            <div className="row gallery">
              {guestHouse.images && guestHouse.images.length > 0
                ? guestHouse.images.slice(0, 3).map((image, index) => (
                    <div className="col-6 mb-3" key={`image-${index}`}>
                      <img
                        src={domain + image.imageUrl}
                        alt={image.imageAltText || `Guest House View ${index + 1}`}
                        className="gallery-img img-fluid"
                        style={{
                          borderRadius: "5px",
                          height: "150px",
                          width: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  ))
                : // Fallback images if no images available
                  [1, 2, 3].map((num) => (
                    <div className="col-6 mb-3" key={`placeholder-${num}`}>
                      <img
                        src={"https://via.placeholder.com/150"}
                        alt={`Guest House View ${num}`}
                        className="gallery-img img-fluid"
                        style={{
                          borderRadius: "5px",
                          height: "150px",
                          width: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  ))}
            </div>
            
            {/* Display Occupied Dates for the selected room */}
            <RoomOccupiedDates selectedRoomId={formData.selectedRoomId} />
            
            {/* File Upload Section */}
            {!bookingId ? (
              <div className="mt-4 p-3 border rounded bg-light">
                <h5 className="mb-3">Upload Documents</h5>
                <p className="text-muted small mb-3">
                  You can upload documents related to this guest house.
                  For booking-specific documents, please complete your booking first.
                </p>
                <form onSubmit={handleFileUpload}>
                  <div className="mb-3">
                    <label htmlFor="fileInput" className="form-label">Select File</label>
                    <input 
                      type="file"
                      className={`form-control ${validationErrors.file ? "is-invalid" : ""}`}
                      id="fileInput"
                      onChange={handleFileChange}
                    />
                    {validationErrors.file && (
                      <div className="invalid-feedback">
                        {validationErrors.file}
                      </div>
                    )}
                    <div className="form-text">
                      Allowed file types: PDF, JPEG, JPG, PNG (Max size: 5MB)
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="fileDescription" className="form-label">Description (Optional)</label>
                    <input 
                      type="text"
                      className="form-control"
                      id="fileDescription"
                      placeholder="Enter a description for this file"
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-outline-primary"
                    disabled={fileUploading}
                  >
                    {fileUploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Uploading...
                      </>
                    ) : (
                      <>Upload File</>
                    )}
                  </button>
                </form>
                
                {/* Display uploaded files */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <h6>Uploaded Files</h6>
                    <ul className="list-group">
                      {uploadedFiles.map((file, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <i className="fas fa-file me-2"></i>
                            {file.fileName}
                          </div>
                          <a 
                            href={fileService.getFileDownloadUrl(file.fileId)} 
                            className="btn btn-sm btn-outline-secondary"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fas fa-download me-1"></i> Download
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="col-md-7">
            <div className="booking-form p-4 border rounded shadow-sm">
              <h3 className="guest-house-book-now mb-4">
                Guest House Book Now
              </h3>

              <form onSubmit={handleSubmit} noValidate>
              <div className="mb-4">
                  <h4 className="form-section-title">Guest Information</h4>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="guestName" className="form-label">
                        Guest Name {requiredField}
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          validationErrors.guestName ? "is-invalid" : ""
                        }`}
                        id="guestName"
                        placeholder="Enter Guest Name"
                        value={formData.guestName}
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.guestName && (
                        <div className="invalid-feedback">
                          {validationErrors.guestName}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="cpfNumber" className="form-label">
                        CPF Number
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="cpfNumber"
                        placeholder="Enter CPF Number"
                        value={formData.cpfNumber}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="emailId" className="form-label">
                        Email Id
                      </label>
                      <input
                        type="email"
                        className={`form-control ${
                          validationErrors.emailId ? "is-invalid" : ""
                        }`}
                        id="emailId"
                        placeholder="Enter Email ID"
                        value={formData.emailId}
                        onChange={handleInputChange}
                      />
                      {validationErrors.emailId && (
                        <div className="invalid-feedback">
                          {validationErrors.emailId}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="phoneNo" className="form-label">
                        Phone No {requiredField}
                      </label>
                      <input
                        type="tel"
                        className={`form-control ${
                          validationErrors.phoneNo ? "is-invalid" : ""
                        }`}
                        id="phoneNo"
                        placeholder="Enter Phone No"
                        value={formData.phoneNo}
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.phoneNo && (
                        <div className="invalid-feedback">
                          {validationErrors.phoneNo}
                        </div>
                      )}
                    </div>
                  </div>
                </div>


                <div className="mb-4">
                  <h4 className="form-section-title">Booking Details</h4>

                  {/* Room Selection Dropdown - Show first before dates */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="selectedRoomId" className="form-label">
                        Select Room {requiredField}
                      </label>
                      <select
                        className={`form-select ${
                          validationErrors.selectedRoomId ? "is-invalid" : ""
                        }`}
                        id="selectedRoomId"
                        value={formData.selectedRoomId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a Room</option>
                        {guestHouse.rooms &&
                          guestHouse.rooms
                            .filter((room) => room.isActive)
                            .map((room) => (
                              <option key={room.roomID} value={room.roomID}>
                                {room.roomName} ({room.roomNumber})
                              </option>
                            ))}
                      </select>
                      {validationErrors.selectedRoomId && (
                        <div className="invalid-feedback">
                          {validationErrors.selectedRoomId}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="travelType" className="form-label">
                        Travel Type {requiredField}
                      </label>
                      <select
                        className={`form-select ${
                          validationErrors.travelType ? "is-invalid" : ""
                        }`}
                        id="travelType"
                        value={formData.travelType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Guest Type</option>
                        <option value="official">Official</option>
                        <option value="personal">Personal</option>
                      </select>
                      {validationErrors.travelType && (
                        <div className="invalid-feedback">
                          {validationErrors.travelType}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date selection with CustomDatePicker */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="checkInDate" className="form-label">
                        Check-In Date/Time {requiredField}
                      </label>
                      <div className="position-relative">
                        <CustomDatePicker
                          id="checkInDate"
                          selectedDate={formData.checkInDate}
                          onChange={handleInputChange}
                          disabledDateRanges={formatDisabledDateRanges(
                            formData.selectedRoomId
                          )}
                          minDate={new Date()}
                          placeholderText="Select check-in date and time"
                          isInvalid={!!validationErrors.checkInDate}
                          errorMessage={validationErrors.checkInDate}
                          showTimeSelect={true}
                          disabled={!formData.selectedRoomId}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="checkOutDate" className="form-label">
                        Check-Out Date/Time {requiredField}
                      </label>
                      <div className="position-relative">
                        <CustomDatePicker
                          id="checkOutDate"
                          selectedDate={formData.checkOutDate}
                          onChange={handleInputChange}
                          disabledDateRanges={formatDisabledDateRanges(
                            formData.selectedRoomId
                          )}
                          minDate={
                            formData.checkInDate
                              ? new Date(
                                  new Date(formData.checkInDate).getTime() +
                                    86400000
                                )
                              : new Date()
                          } // Add 1 day (86400000 ms)
                          placeholderText="Select check-out date and time"
                          isInvalid={!!validationErrors.checkOutDate}
                          errorMessage={validationErrors.checkOutDate}
                          showTimeSelect={true}
                          disabled={!formData.checkInDate}
                          // Additional filter to ensure check-out is after check-in
                          filterDate={(date) => {
                            if (!formData.checkInDate) return true;
                            return date > new Date(formData.checkInDate);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {validationErrors.dateConflict && (
                    <div className="alert alert-danger py-2 mb-3">
                      {validationErrors.dateConflict}
                    </div>
                  )}

                  <div className="row mb-3">
                    {/* Display Room Type and Room Number as text */}
                    <div className="col-md-6">
                      <label htmlFor="roomType" className="form-label">
                        Room Type
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="roomType"
                        value={formData.roomType}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="roomNumber" className="form-label">
                        Room Number
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="roomNumber"
                        value={formData.roomNumber}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label htmlFor="roomRent" className="form-label">
                        Room Rent
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="text"
                          className="form-control"
                          id="roomRent"
                          value={formData.roomRent}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="finalRoomRent" className="form-label">
                        Final Room Rent
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="text"
                          className="form-control"
                          id="finalRoomRent"
                          value={formData.finalRoomRent}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-12 mt-4">
                      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between">
                        <p className="text-danger mb-2 mb-md-0">
                          <small>
                            Fields marked with {requiredField} are required
                          </small>
                        </p>
                        <button
                          type="submit"
                          className="btn btn-danger px-4 py-2"
                        >
                          BOOK NOW
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* <div className="col-12 mt-4">
                  <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between">
                    <p className="text-danger mb-2 mb-md-0">
                      <small>Fields marked with {requiredField} are required</small>
                    </p>
                    <button
                      type="submit"
                      className="btn btn-danger px-4 py-2"
                    >
                      BOOK NOW
                    </button>
                  </div>
                </div> */}
              </form>
            </div>
          </div>
        </div>
      </main>
      
      {/* Success Modal - Will only show when showSuccessModal is true */}
      {/* <BookingSuccessModal 
        isOpen={showSuccessModal} 
        onClose={handleCloseModal} 
      /> */}
      
      <Footer />
    </>
  );
};

export default GuestHouseDetail;
